import {useCallback} from 'react'
import {
  ThemeDetailFragment,
  useAddThemeMutation,
  useGetThemeQuery
} from '../data/workbench-api/generated/theme'
import {Theme} from '../data/workbench-api/generated/types'
import {useEnvironment} from '../environment'
import {useAnalytics} from '../lib/analytics/analytics'

type ThemeIdentifiers = Pick<Theme, 'id' | 'environmentId'>

export const useDuplicateTheme = (): [
  (themeIdentifiers: ThemeIdentifiers) => Promise<ThemeDetailFragment>,
  {loading: boolean}
] => {
  const analytics = useAnalytics()
  const {environment} = useEnvironment()
  const [doMutation, data] = useAddThemeMutation({
    refetchQueries: ['ThemeList']
  })

  // skipped query hack allows us to call it imperatively and wait on the result
  // (note, lazyQuery functions return void) ¯\_(ツ)_/¯
  const {refetch} = useGetThemeQuery({skip: true})

  const duplicateTheme = useCallback(
    async (variables: ThemeIdentifiers) => {
      const query = await refetch(variables)

      const themeData = query.data.theme

      if (!themeData) {
        throw new Error('Could not retrieve Theme for duplication.')
      }

      const result = await doMutation({
        variables: {
          environmentId: themeData.environmentId,
          name: newThemeName(themeData.name),
          payload: themeData.payload
        },
        onCompleted: () => {
          if (environment) {
            analytics.track({
              event: 'Added configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category: 'configuration>sdk>container-themes'
              }
            })
          }
        }
      })

      const newTheme = result.data?.createTheme?.theme

      if (!newTheme) {
        throw new Error('Could not create duplicate theme')
      }

      return newTheme
    },
    [doMutation, refetch, environment, analytics]
  )

  return [duplicateTheme, {loading: data.loading}]
}

const suffixRegex = / \((\d+)\)$/
export const newThemeName = (oldName: string): string => {
  if (oldName.slice(0, 8) === 'Copy of ') {
    const match = oldName.match(suffixRegex)
    if (match) {
      return oldName.replace(suffixRegex, ` (${Number(match[1]) + 1})`)
    }

    return `${oldName} (2)`
  }

  return `Copy of ${oldName}`
}
